import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Bot, Square, Camera, Terminal } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, API, TOKEN_KEY } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, CodeBadge } from "@/components/StatusBadge";
import { JobTimeline } from "@/components/bot/JobTimeline";
import { TwoFAPanel } from "@/components/bot/TwoFAPanel";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const ACTIVE = ["queued", "running", "waiting_2fa"];

export default function BotPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [job, setJob] = useState(null);
  const [shotKey, setShotKey] = useState(0);

  const loadList = () => api.get("/jobs").then((r) => { setJobs(r.data); if (!jobId && r.data[0]) navigate(`/bot/${r.data[0].id}`, { replace: true }); });
  const loadJob = () => jobId && api.get(`/jobs/${jobId}`).then((r) => { setJob(r.data); if (r.data.screenshot_at) setShotKey(r.data.screenshot_at); }).catch(() => setJob(null));

  useEffect(() => { loadList(); }, [jobId]); // eslint-disable-line
  useEffect(() => { loadJob(); }, [jobId]); // eslint-disable-line
  useEffect(() => {
    const t = setInterval(() => { loadJob(); if (jobs.some((j) => ACTIVE.includes(j.status))) loadList(); }, 2500);
    return () => clearInterval(t);
  }, [jobId, jobs]); // eslint-disable-line

  const cancel = async () => { try { await api.post(`/jobs/${jobId}/cancel`); toast.success("Cancelación solicitada"); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div className="space-y-6">
      <div>
        <p className="eyebrow">Automatización</p>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Bot Downtown</h1>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-4 panel p-3 max-h-[75vh] overflow-y-auto" data-testid="jobs-list">
          {jobs.length === 0 && <p className="text-sm text-muted-foreground p-4" data-testid="jobs-list-empty">No hay ejecuciones. Envía un lanzamiento desde el catálogo.</p>}
          {jobs.map((j) => (
            <button key={j.id} onClick={() => navigate(`/bot/${j.id}`)} data-testid={`job-item-${j.id}`}
              className={cn("w-full text-left rounded-md p-3 mb-1 transition-colors border", j.id === jobId ? "bg-primary/10 border-primary/40" : "border-transparent hover:bg-muted")}>
              <div className="flex items-center justify-between gap-2"><p className="text-sm font-medium truncate">{j.release_title}</p><StatusBadge status={j.status} testId="job-status-badge" /></div>
              <p className="text-xs text-muted-foreground mt-1 truncate">{j.artist_name} · {new Date(j.created_at).toLocaleString()} · {j.mode || "—"}</p>
            </button>
          ))}
        </aside>
        <section className="lg:col-span-8 space-y-6">
          {!job ? <div className="panel p-12 text-center text-muted-foreground text-sm">Selecciona una ejecución.</div> : (
            <>
              <div className="panel p-6 flex flex-wrap items-start justify-between gap-4 rise">
                <div className="flex gap-4 items-start">
                  <div className={cn("h-11 w-11 rounded-md grid place-items-center shrink-0", ACTIVE.includes(job.status) ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground")}><Bot className="h-5 w-5" /></div>
                  <div>
                    <Link to={job.kind === "profile" ? "/artistas" : `/lanzamientos/${job.release_id}`} className="font-display text-xl font-bold hover:text-primary" data-testid="job-release-link">{job.release_title}</Link>
                    <p className="text-sm text-muted-foreground">{job.artist_name} · modo <span className="font-mono uppercase" data-testid="job-mode">{job.mode || "pendiente"}</span> · iniciado por {job.triggered_by}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge status={job.status} testId="job-detail-status" />
                  {ACTIVE.includes(job.status) && can("admin", "manager") && <Button variant="outline" size="sm" onClick={cancel} data-testid="btn-cancel-job" className="gap-1.5"><Square className="h-3.5 w-3.5" /> Cancelar</Button>}
                </div>
              </div>
              {job.status === "waiting_2fa" && <TwoFAPanel job={job} onSubmitted={loadJob} />}
              {job.error && <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-300" data-testid="job-error">{job.error}</div>}
              {job.result && job.kind === "profile" && (
                <div className="rounded-lg border border-primary/40 bg-primary/10 p-5 space-y-1 rise" data-testid="job-result">
                  <p className="font-display font-semibold">Perfil creado en Downtown {job.result.simulated && <span className="text-xs font-normal text-amber-500 ml-2">(simulado)</span>}</p>
                  {job.result.downtown_url && <a href={job.result.downtown_url} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline inline-block pt-1" data-testid="job-downtown-link">Abrir en Downtown{job.result.downtown_artist_id ? ` (#${job.result.downtown_artist_id})` : ""} →</a>}
                </div>
              )}
              {job.result && job.kind !== "profile" && (
                <div className="rounded-lg border border-primary/40 bg-primary/10 p-5 space-y-2 rise" data-testid="job-result">
                  <p className="font-display font-semibold">Códigos asignados {job.mode === "simulation" && <span className="text-xs font-normal text-amber-500 ml-2">(simulados)</span>}</p>
                  <div className="flex items-center gap-2 text-sm"><span className="text-muted-foreground w-12">UPC</span><CodeBadge code={job.result.upc} kind="upc" /></div>
                  {Object.entries(job.result.isrcs || {}).map(([tid, code], i) => <div key={tid} className="flex items-center gap-2 text-sm"><span className="text-muted-foreground w-12">ISRC {i + 1}</span><CodeBadge code={code} kind="isrc" /></div>)}
                  {job.result.downtown_url && <a href={job.result.downtown_url} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline inline-block pt-1" data-testid="job-downtown-link">Abrir álbum #{job.result.downtown_release_id} en Downtown →</a>}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="panel p-6"><h2 className="font-display font-semibold mb-4">Progreso</h2><JobTimeline steps={job.steps} /></div>
                <div className="space-y-6">
                  <div className="panel p-4">
                    <h2 className="font-display font-semibold mb-3 flex items-center gap-2 text-sm"><Camera className="h-4 w-4" /> Vista del navegador</h2>
                    {job.screenshot_at ? <img key={shotKey} src={`${API}/jobs/${job.id}/screenshot?auth=${localStorage.getItem(TOKEN_KEY)}&t=${shotKey}`} alt="captura del bot" className="rounded-md border border-border w-full" data-testid="job-screenshot" />
                      : <p className="text-xs text-muted-foreground" data-testid="job-screenshot-empty">{job.mode === "simulation" ? "Sin captura en modo simulación." : "Aún no hay captura."}</p>}
                  </div>
                  <div className="panel p-4">
                    <h2 className="font-display font-semibold mb-3 flex items-center gap-2 text-sm"><Terminal className="h-4 w-4" /> Registro</h2>
                    <div className="font-mono text-[11px] space-y-1 max-h-72 overflow-y-auto" data-testid="job-logs">
                      {job.logs.map((l, i) => <p key={i} className={cn(l.level === "error" ? "text-red-400" : l.level === "warn" ? "text-amber-400" : "text-muted-foreground")}><span className="opacity-50">{new Date(l.at).toLocaleTimeString()}</span> {l.msg}</p>)}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
